export const aboutPageStyles = `
@font-face {
  font-family: "GTF Good Mono";
  src: url("/fonts/GoodMono/GTFGoodMonoTRIAL-Thin.otf") format("opentype");
  font-weight: 100;
  font-display: swap;
}

@keyframes zmk-about-bloom {
  0%,
  100% {
    opacity: 0.82;
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate3d(0, -1.5%, 0) scale(1.02);
  }
}

.zmk-about-root {
  --zmk-bg: var(--zmk-palette-bg);
  --zmk-bg-deep: var(--zmk-palette-deep-bg);
  --zmk-ink: var(--zmk-palette-ink);
  --zmk-copy: color-mix(in srgb, var(--zmk-ink) 68%, transparent);
  --zmk-muted: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  --zmk-faint: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  --zmk-line: color-mix(in srgb, var(--zmk-ink) 12%, transparent);
  --zmk-line-soft: color-mix(in srgb, var(--zmk-ink) 6%, transparent);
  --zmk-blue: var(--zmk-palette-blue);
  --zmk-blue-hover: var(--zmk-palette-blue-hover);
  --zmk-ease: cubic-bezier(0.16, 1, 0.3, 1);

  background: var(--zmk-bg);
  color: var(--zmk-ink);
  color-scheme: dark;
  font-family: var(--font-brand-stack);
  font-weight: 300;
  min-height: 100svh;
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;
}

.zmk-about-root *,
.zmk-about-root *::before,
.zmk-about-root *::after {
  box-sizing: border-box;
}

.zmk-about-root a {
  color: inherit;
  text-decoration: none;
}

.zmk-about-root button,
.zmk-about-root input,
.zmk-about-root textarea,
.zmk-about-root select {
  font-family: var(--font-brand-stack);
}

.zmk-about-root ::selection {
  background: color-mix(in srgb, var(--zmk-blue) 35%, transparent);
  color: var(--zmk-ink);
}

.zmk-about-nav {
  background: transparent;
  border-bottom: 1px solid transparent;
  inset-inline: 0;
  position: fixed;
  top: 0;
  transform: translateY(0);
  transition:
    background 0.3s ease,
    border-color 0.3s ease,
    transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 100;
}

/* Reference values: scrim past 80px, retract on downward scroll. */
.zmk-about-nav.is-scrolled {
  background: color-mix(in srgb, var(--zmk-bg) 86%, transparent);
  border-bottom-color: color-mix(in srgb, var(--ld-surface-white) 6%, transparent);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.zmk-about-nav.is-hidden {
  transform: translateY(-100%);
}

.zmk-about-nav__inner {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1440px;
  padding: 20px 48px;
}

.zmk-about-nav__brand {
  align-items: center;
  display: flex;
}

.zmk-about-nav__brand img {
  display: block;
  height: 34px;
  object-fit: contain;
  width: auto;
}

.zmk-about-nav__links {
  align-items: center;
  display: flex;
  gap: 40px;
}

.zmk-about-nav__links a,
.zmk-about-nav__request {
  color: var(--zmk-ink) !important;
  font-size: 15px !important;
  font-weight: 400 !important;
  height: auto !important;
  line-height: 1.2;
  padding: 0 !important;
  position: relative;
  transition: color 0.2s ease;
}

.zmk-about-nav__links a::after,
.zmk-about-nav__request::after {
  background: color-mix(in srgb, var(--zmk-ink) 85%, transparent);
  bottom: -6px;
  content: "";
  height: 1.5px;
  left: 0;
  position: absolute;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.18s ease;
  width: 100%;
}

.zmk-about-nav__links a:hover,
.zmk-about-nav__request:hover {
  background: transparent !important;
  color: color-mix(in srgb, var(--zmk-ink) 65%, transparent) !important;
}

.zmk-about-nav__links a:hover::after,
.zmk-about-nav__request:hover::after {
  transform: scaleX(1);
}

.zmk-about-nav__menu {
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 16%, transparent) !important;
  color: var(--zmk-ink) !important;
  display: none !important;
  height: 40px !important;
  width: 40px !important;
}

.zmk-about-nav__menu svg {
  height: 18px;
  width: 18px;
}

.zmk-about-mobile-menu {
  background: color-mix(in srgb, var(--zmk-bg-deep) 96%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--zmk-ink) 8%, transparent);
  display: none;
  flex-direction: column;
  gap: 22px;
  inset: 73px 0 auto;
  padding: 26px 24px 30px;
  position: fixed;
  transform: translateY(-10px);
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 0.24s ease,
    transform 0.24s ease;
  z-index: 99;
}

.zmk-about-mobile-menu.is-open {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.zmk-about-mobile-menu a {
  color: color-mix(in srgb, var(--zmk-ink) 86%, transparent);
  font-size: 20px;
}

.zmk-about-button {
  align-items: center;
  background: var(--zmk-blue) !important;
  border: 1px solid color-mix(in srgb, var(--ld-surface-white) 12%, transparent) !important;
  border-radius: 8px !important;
  box-shadow: 0 0 20px 1px color-mix(in srgb, var(--zmk-blue) 13%, transparent);
  color: var(--zmk-ink) !important;
  display: inline-flex;
  font-size: 16px !important;
  font-weight: 400 !important;
  gap: 10px;
  min-height: 48px !important;
  padding: 14px 28px !important;
  transition:
    filter 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.2s ease !important;
}

.zmk-about-button:hover {
  box-shadow: 0 0 16px 1px color-mix(in srgb, var(--zmk-blue) 22%, transparent);
  filter: brightness(1.14);
  transform: translateY(-2px);
}

.zmk-about-button svg {
  height: 15px;
  width: 15px;
}

.zmk-about-section {
  border-top: 1px solid var(--zmk-line-soft);
  position: relative;
}

.zmk-about-section:first-of-type {
  border-top: 0;
}

.zmk-about-wrap {
  margin: 0 auto;
  max-width: 1120px;
  padding-left: 48px;
  padding-right: 48px;
  position: relative;
  z-index: 1;
}

.zmk-about-bloom,
.zmk-about-grain {
  inset: 0;
  pointer-events: none;
  position: absolute;
}

.zmk-about-bloom {
  animation: zmk-about-bloom 30s ease-in-out infinite;
  background: radial-gradient(
    ellipse 58% 50% at 30% 34%,
    color-mix(in srgb, var(--zmk-palette-bloom-warm) 34%, transparent) 0%,
    color-mix(in srgb, var(--zmk-palette-bloom-shadow) 16%, transparent) 46%,
    color-mix(in srgb, var(--zmk-bg) 0%, transparent) 74%
  );
}

.zmk-about-bloom--cta {
  background: radial-gradient(
    ellipse 58% 48% at 26% 64%,
    color-mix(in srgb, var(--zmk-palette-bloom-warm) 36%, transparent) 0%,
    color-mix(in srgb, var(--zmk-palette-bloom-shadow) 18%, transparent) 46%,
    color-mix(in srgb, var(--zmk-bg) 0%, transparent) 74%
  );
}

.zmk-about-grain {
  background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='240' height='240' filter='url(%23n)'/%3E%3C/svg%3E");
  mix-blend-mode: overlay;
  opacity: 0.05;
}

.zmk-about-mono {
  color: var(--zmk-faint);
  font-family: "GTF Good Mono", monospace !important;
  font-size: 10.5px;
  font-weight: 100;
  letter-spacing: 0;
  margin: 0;
  text-transform: uppercase;
}

.zmk-about-display {
  color: var(--zmk-ink);
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 1.14;
  margin: 0;
  text-wrap: balance;
}

.zmk-about-hero {
  overflow: hidden;
}

.zmk-about-hero .zmk-about-wrap {
  padding-bottom: 190px;
  padding-top: 190px;
}

.zmk-about-hero h1 {
  font-size: 72px;
  max-width: 980px;
}

.zmk-about-hero p:not(.zmk-about-mono) {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 19px;
  line-height: 1.66;
  margin: 38px 0 0;
  max-width: 720px;
  text-wrap: pretty;
}

.zmk-about-hero .zmk-about-mono,
.zmk-about-statement .zmk-about-mono,
.zmk-about-cta .zmk-about-mono {
  margin-bottom: 34px;
}

.zmk-about-team .zmk-about-wrap {
  padding-bottom: 106px;
  padding-top: 106px;
}

.zmk-about-team .zmk-about-sline {
  margin-top: 44px;
}

.zmk-about-team__list {
  margin-top: 0;
}

.zmk-about-team__row {
  align-items: center;
  border-bottom: 1px solid var(--zmk-line);
  display: grid;
  gap: 30px;
  grid-template-columns: 72px minmax(0, 1fr) auto;
  min-height: 128px;
  padding: 28px 0;
  transition:
    padding-left 0.3s var(--zmk-ease),
    background-color 0.3s ease;
}

.zmk-about-team__row:hover {
  background: color-mix(in srgb, var(--zmk-ink) 1.2%, transparent);
  padding-left: 12px;
}

.zmk-about-team__image {
  border-radius: 999px;
  height: 72px;
  overflow: hidden;
  position: relative;
  width: 72px;
}

.zmk-about-team__image img {
  object-fit: cover;
}

.zmk-about-team__row > span:nth-child(2) {
  display: block;
  min-width: 0;
}

.zmk-about-team__name {
  color: var(--zmk-ink);
  display: block;
  font-family: var(--font-libre-caslon-display), Georgia, serif;
  font-size: 30px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 0;
}

.zmk-about-team__title {
  display: block;
  margin-top: 10px;
  overflow-wrap: anywhere;
}

.zmk-about-team__icon {
  color: color-mix(in srgb, var(--zmk-ink) 34%, transparent);
  height: 17px;
  transition:
    color 0.2s ease,
    transform 0.2s ease;
  width: 17px;
}

.zmk-about-team__row:hover .zmk-about-team__icon {
  color: color-mix(in srgb, var(--zmk-ink) 70%, transparent);
  transform: translateX(2px);
}

.zmk-about-statement {
  overflow: hidden;
}

.zmk-about-statement .zmk-about-wrap {
  padding-bottom: 140px;
  padding-top: 140px;
}

.zmk-about-statement h2 {
  font-size: 60px;
  max-width: 820px;
}

.zmk-about-statement p:not(.zmk-about-mono) {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 18px;
  line-height: 1.68;
  margin: 34px 0 0;
  max-width: 560px;
}

.zmk-about-memory .zmk-about-wrap {
  padding-bottom: 120px;
  padding-top: 120px;
}

.zmk-about-memory h2 {
  font-size: 54px;
  max-width: 760px;
}

.zmk-about-memory p:not(.zmk-about-mono) {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 18px;
  line-height: 1.68;
  margin: 32px 0 0;
  max-width: 660px;
  text-wrap: pretty;
}

.zmk-about-partner .zmk-about-wrap {
  padding-bottom: 126px;
  padding-top: 126px;
}

.zmk-about-partner h2 {
  font-size: 54px;
  max-width: 780px;
}

.zmk-about-partner__lede {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 18px;
  line-height: 1.68;
  margin: 30px 0 0;
  max-width: 620px;
  text-wrap: pretty;
}

.zmk-about-partner .zmk-about-sline {
  margin-top: 48px;
}

.zmk-about-partner__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.zmk-about-partner__row {
  align-items: start;
  border-bottom: 1px solid var(--zmk-line);
  display: grid;
  gap: 30px;
  grid-template-columns: 72px minmax(0, 1fr);
  padding: 30px 0;
}

.zmk-about-partner__number {
  margin: 0;
  padding-top: 4px;
}

.zmk-about-partner__body {
  display: block;
  max-width: 760px;
}

.zmk-about-partner__title {
  color: var(--zmk-ink);
  display: block;
  font-size: 21px;
  font-weight: 400;
  line-height: 1.35;
  margin-bottom: 10px;
}

.zmk-about-partner__copy {
  color: color-mix(in srgb, var(--zmk-ink) 58%, transparent);
  display: block;
  font-size: 16px;
  line-height: 1.66;
  text-wrap: pretty;
}

.zmk-about-partner__note {
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  font-size: 14px;
  line-height: 1.6;
  margin: 30px 0 0;
  max-width: 560px;
}

.zmk-about-cta__secondary {
  color: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  font-size: 15px;
  line-height: 1.6;
  margin: 30px 0 0;
}

.zmk-about-cta__secondary a {
  color: var(--zmk-ink);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.zmk-about-founder .zmk-about-wrap {
  display: grid;
  gap: 84px;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.6fr);
  padding-bottom: 118px;
  padding-top: 118px;
}

.zmk-about-founder__identity {
  margin-top: 70px;
}

.zmk-about-founder__name {
  color: var(--zmk-ink);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4;
  margin: 0;
}

.zmk-about-founder__role {
  color: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  font-size: 13px;
  line-height: 1.5;
  margin: 6px 0 0;
}

.zmk-about-founder__quote {
  max-width: 560px;
}

.zmk-about-founder__quote p {
  color: color-mix(in srgb, var(--zmk-ink) 72%, transparent);
  font-size: 16px;
  line-height: 1.72;
  margin: 0;
}

.zmk-about-founder__quote p + p {
  margin-top: 28px;
}

.zmk-about-news {
  background: var(--zmk-bg-deep);
}

.zmk-about-news .zmk-about-wrap {
  max-width: 916px;
  padding-bottom: 110px;
  padding-top: 110px;
}

.zmk-about-news .zmk-about-sline {
  margin-top: 38px;
}

.zmk-about-news__item {
  border-bottom: 1px solid var(--zmk-line);
  margin-top: 0;
}

.zmk-about-news__toggle {
  align-items: center;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  color: var(--zmk-ink) !important;
  display: grid !important;
  flex-shrink: 1 !important;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) auto;
  height: auto !important;
  min-width: 0;
  padding: 26px 0 !important;
  text-align: left;
  white-space: normal !important;
  width: 100%;
}

.zmk-about-news__toggle > span:first-child {
  display: block;
  min-width: 0;
}

.zmk-about-news__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-bottom: 12px;
}

.zmk-about-news__tags span {
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 10px;
  font-weight: 100;
  letter-spacing: 0;
  text-transform: uppercase;
}

.zmk-about-news__headline {
  color: var(--zmk-ink);
  display: block;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.45;
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
}

.zmk-about-news__byline {
  color: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  display: block;
  font-size: 15px;
  font-weight: 300;
  margin: 12px 0 0;
  min-width: 0;
  overflow-wrap: anywhere;
}

/* The plus stays white until the item is opened, then rotates into a cross and
   takes the accent, matching the reference (data-news-icon in
   "Zom Marketing Site.dc.html": rotate(45deg) + the blue accent on open). */
.zmk-about-news__icon {
  border: 1px solid color-mix(in srgb, var(--zmk-ink) 16%, transparent);
  border-radius: 999px;
  height: 28px;
  position: relative;
  transition:
    transform 0.3s var(--zmk-ease),
    border-color 0.25s ease;
  width: 28px;
}

.zmk-about-news__icon::before,
.zmk-about-news__icon::after {
  background: var(--zmk-ink);
  content: "";
  left: 8px;
  position: absolute;
  top: 13px;
  transition: background 0.25s ease;
}

.zmk-about-news__icon::before {
  height: 1px;
  width: 10px;
}

.zmk-about-news__icon::after {
  height: 10px;
  left: 12.5px;
  top: 8px;
  width: 1px;
}

.zmk-about-news__toggle[aria-expanded="true"] .zmk-about-news__icon {
  border-color: color-mix(in srgb, var(--zmk-blue-hover) 60%, transparent);
  transform: rotate(45deg);
}

.zmk-about-news__toggle[aria-expanded="true"] .zmk-about-news__icon::before,
.zmk-about-news__toggle[aria-expanded="true"] .zmk-about-news__icon::after {
  background: var(--zmk-blue-hover);
}

.zmk-about-news__body {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows 0.44s var(--zmk-ease),
    opacity 0.24s ease;
}

.zmk-about-news__body.is-open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.zmk-about-news__body-inner {
  min-height: 0;
  overflow: hidden;
  transform: translateY(-4px);
  transition: transform 0.44s var(--zmk-ease);
}

.zmk-about-news__body.is-open .zmk-about-news__body-inner {
  transform: translateY(0);
}

.zmk-about-news__body-content {
  color: color-mix(in srgb, var(--zmk-ink) 62%, transparent);
  font-size: 15px;
  line-height: 1.7;
  max-width: 680px;
  padding-bottom: 30px;
}

.zmk-about-news__body-content p {
  margin: 0;
}

.zmk-about-news__body-content p + p {
  margin-top: 18px;
}

.zmk-about-news__link {
  align-items: center;
  color: var(--zmk-blue-hover) !important;
  display: inline-flex;
  gap: 8px;
  margin-top: 22px;
  text-decoration: underline !important;
  text-decoration-color: color-mix(in srgb, var(--zmk-blue-hover) 40%, transparent) !important;
  text-decoration-thickness: 1px !important;
  text-underline-offset: 5px;
}

.zmk-about-news__link svg {
  height: 14px;
  width: 14px;
}

.zmk-about-cta {
  overflow: hidden;
}

.zmk-about-cta .zmk-about-wrap {
  padding-bottom: 142px;
  padding-top: 142px;
}

.zmk-about-cta h2 {
  font-size: 64px;
  max-width: 880px;
}

.zmk-about-cta__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-top: 42px;
}

.zmk-about-cta__email {
  color: color-mix(in srgb, var(--zmk-ink) 50%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 10.5px;
  font-weight: 100;
  letter-spacing: 0;
  text-transform: uppercase;
}

.zmk-about-footer {
  background: var(--zmk-palette-footer);
  border-top: 1px solid var(--zmk-line-soft);
}

.zmk-about-footer__inner {
  margin: 0 auto;
  max-width: 1320px;
  padding: 92px 48px 48px;
}

.zmk-about-footer__grid {
  display: grid;
  gap: 80px;
  grid-template-columns: minmax(0, 1.35fr) repeat(2, minmax(120px, 0.45fr));
}

.zmk-about-footer__brand img {
  display: block;
  height: 34px;
  width: auto;
}

.zmk-about-footer__brand p {
  color: color-mix(in srgb, var(--zmk-ink) 58%, transparent);
  font-size: 16px;
  line-height: 1.5;
  margin: 28px 0 0;
  max-width: 360px;
}

.zmk-about-footer__col {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.zmk-about-footer__col h3 {
  color: color-mix(in srgb, var(--zmk-ink) 38%, transparent);
  font-family: "GTF Good Mono", monospace;
  font-size: 10px;
  font-weight: 100;
  letter-spacing: 0;
  margin: 0 0 4px;
  text-transform: uppercase;
}

.zmk-about-footer__col a,
.zmk-about-footer__col button {
  color: color-mix(in srgb, var(--zmk-ink) 78%, transparent) !important;
  font-size: 15px !important;
  font-weight: 300 !important;
  height: auto !important;
  justify-content: flex-start !important;
  padding: 0 !important;
  text-align: left;
}

.zmk-about-footer__col button:hover {
  background: transparent !important;
}

.zmk-about-footer__bottom {
  border-top: 1px solid var(--zmk-line);
  color: color-mix(in srgb, var(--zmk-ink) 42%, transparent);
  font-size: 14px;
  margin-top: 84px;
  padding-top: 34px;
}

.zmk-about-root [data-sreveal] {
  opacity: 0;
  transform: translate3d(0, 18px, 0);
  transition:
    opacity 0.7s var(--zmk-ease),
    transform 0.7s var(--zmk-ease);
}

.zmk-about-root [data-sreveal].is-visible {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

/* Match landing/security hairlines: left-origin scaleX draw, 1.2s. */
.zmk-about-sline,
.zmk-about-root [data-sline] {
  background: var(--zmk-line);
  display: block;
  height: 1px;
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 1.2s var(--zmk-ease);
  width: 100%;
}

.zmk-about-root [data-sline].is-visible {
  transform: scaleX(1);
}

@media (max-width: 1023px) {
  .zmk-about-partner h2,
  .zmk-about-memory h2 {
    font-size: 42px;
  }

  .zmk-about-nav__links {
    gap: 26px;
  }

  .zmk-about-hero .zmk-about-wrap {
    padding-bottom: 116px;
    padding-top: 136px;
  }

  .zmk-about-hero h1 {
    font-size: 58px;
  }

  .zmk-about-hero p:not(.zmk-about-mono) {
    font-size: 18px;
    margin-top: 30px;
    max-width: 620px;
  }

  .zmk-about-team .zmk-about-wrap {
    padding-bottom: 88px;
    padding-top: 88px;
  }

  .zmk-about-team__row {
    gap: 24px;
    grid-template-columns: 56px minmax(0, 1fr) auto;
    min-height: 104px;
    padding: 24px 0;
  }

  .zmk-about-team__image {
    height: 56px;
    width: 56px;
  }

  .zmk-about-team__name {
    font-size: 26px;
  }

  .zmk-about-statement .zmk-about-wrap {
    padding-bottom: 104px;
    padding-top: 104px;
  }

  .zmk-about-statement h2 {
    font-size: 50px;
  }

  .zmk-about-founder .zmk-about-wrap {
    gap: 36px;
    grid-template-columns: minmax(0, 1fr);
    padding-bottom: 88px;
    padding-top: 80px;
  }

  .zmk-about-founder__identity {
    margin-top: 36px;
  }

  .zmk-about-news .zmk-about-wrap {
    padding-bottom: 88px;
    padding-top: 88px;
  }

  .zmk-about-cta .zmk-about-wrap {
    padding-bottom: 116px;
    padding-top: 108px;
  }

  .zmk-about-cta h2 {
    font-size: 54px;
  }

  .zmk-about-footer__grid {
    gap: 44px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .zmk-about-footer__brand {
    grid-column: 1 / -1;
  }
}

@media (max-width: 767px) {
  .zmk-about-memory .zmk-about-wrap {
    padding-bottom: 80px;
    padding-top: 80px;
  }

  .zmk-about-memory h2 {
    font-size: 32px;
  }

  .zmk-about-memory p:not(.zmk-about-mono) {
    font-size: 16px;
    max-width: 100%;
  }

  .zmk-about-partner .zmk-about-wrap {
    padding-bottom: 84px;
    padding-top: 84px;
  }

  .zmk-about-partner h2 {
    font-size: 32px;
  }

  .zmk-about-partner__lede {
    font-size: 16px;
  }

  .zmk-about-partner__row {
    gap: 10px;
    grid-template-columns: minmax(0, 1fr);
    padding: 24px 0;
  }

  .zmk-about-partner__title {
    font-size: 19px;
  }

  .zmk-about-nav__inner {
    padding: 16px 20px;
  }

  .zmk-about-nav__brand img {
    height: 28px;
  }

  .zmk-about-nav__links {
    display: none;
  }

  .zmk-about-nav__menu {
    display: inline-flex !important;
  }

  .zmk-about-mobile-menu {
    display: flex;
  }

  .zmk-about-wrap {
    padding-left: 24px;
    padding-right: 24px;
  }

  .zmk-about-hero .zmk-about-wrap {
    padding-bottom: 104px;
    padding-top: 128px;
  }

  .zmk-about-hero h1 {
    font-size: 40px;
  }

  .zmk-about-hero p:not(.zmk-about-mono) {
    font-size: 16px;
    margin-top: 24px;
    max-width: 100%;
  }

  .zmk-about-hero .zmk-about-mono,
  .zmk-about-statement .zmk-about-mono,
  .zmk-about-cta .zmk-about-mono {
    margin-bottom: 26px;
  }

  .zmk-about-team .zmk-about-wrap {
    padding-bottom: 74px;
    padding-top: 76px;
  }

  .zmk-about-team__list {
    margin-top: 34px;
  }

  .zmk-about-team__row {
    gap: 18px;
    grid-template-columns: 54px minmax(0, 1fr) auto;
    min-height: 96px;
    padding: 20px 0;
  }

  .zmk-about-team__row:hover {
    padding-left: 0;
  }

  .zmk-about-team__image {
    height: 54px;
    width: 54px;
  }

  .zmk-about-team__name {
    font-size: 22px;
  }

  .zmk-about-team__title {
    font-size: 9.5px;
    margin-top: 7px;
  }

  .zmk-about-statement .zmk-about-wrap {
    padding-bottom: 88px;
    padding-top: 86px;
  }

  .zmk-about-statement h2,
  .zmk-about-cta h2 {
    font-size: 38px;
  }

  .zmk-about-statement p:not(.zmk-about-mono) {
    font-size: 16px;
    margin-top: 28px;
  }

  .zmk-about-founder .zmk-about-wrap {
    padding-bottom: 78px;
    padding-top: 72px;
  }

  .zmk-about-news .zmk-about-wrap {
    padding-bottom: 82px;
    padding-top: 78px;
  }

  .zmk-about-news__toggle {
    gap: 14px;
  }

  .zmk-about-news__headline {
    font-size: 16px;
  }

  .zmk-about-cta .zmk-about-wrap {
    padding-bottom: 98px;
    padding-top: 92px;
  }

  .zmk-about-cta__actions {
    align-items: stretch;
    flex-direction: column;
  }

  .zmk-about-button {
    justify-content: center;
    width: 100%;
  }

  .zmk-about-footer__inner {
    padding: 76px 24px 38px;
  }

  .zmk-about-footer__grid {
    gap: 36px;
    grid-template-columns: minmax(0, 1fr);
  }

  .zmk-about-footer__bottom {
    margin-top: 54px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .zmk-about-root *,
  .zmk-about-root *::before,
  .zmk-about-root *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 0.001ms !important;
  }

  .zmk-about-root [data-sreveal],
  .zmk-about-root [data-sline] {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 767px) {
  .zmk-about-root [data-sline] {
    transition-duration: 0.9s;
  }
}
`;
